/**
 * The login screen (PHASE-0.md §2.1).
 *
 * **The banner is here, above everything.** §2.1 asks for it "från och med denna skärm" —
 * from this screen onwards — so it is not something the app puts up after signing in. A
 * clinician must know which system they are typing a password into before they type it: the
 * password that works here is not their Cosmic password, and a login form with no banner
 * is the one screen where that confusion starts.
 *
 * **The language selector lives here**, which is `I18N.md` §6's requirement and the reason
 * it is worded the way it is: a foreign medical team arriving in the wartime scenario must
 * not have to log in through a Swedish interface first. It was in the app bar until this
 * screen existed — see `LanguageChoice`.
 *
 * **Under ten seconds including the page load** is §2.1's acceptance criterion, which is
 * why there is nothing here to wait for: no logo fetch, no font, no probe of anything, and
 * the username field has the cursor on arrival. The one request is the submit.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LanguageChoice } from '../i18n/LanguageChoice'
import { DeploymentNotice } from '../DeploymentNotice'
import { TranslationNotice } from '../i18n/TranslationNotice'
import { MarkLockup } from '../Mark'
import { login, LoginRefusedError, type Session } from './authApi'
import { authUnavailableMessage, loginRefusalMessage } from './authDisplay'

type State =
  | { readonly kind: 'idle' }
  | { readonly kind: 'submitting' }
  /**
   * Something to show the clinician. Already a sentence, not a code.
   *
   * `source` is what decides whether the cursor moves. A `server` refusal means the pair was
   * wrong, so the password is what gets retyped; a `form` refusal means a field was left empty,
   * and moving the cursor to the password would be wrong half the time.
   */
  | { readonly kind: 'refused'; readonly message: string; readonly source: 'form' | 'server' }

/**
 * The login screen.
 *
 * @param onSignedIn Called with the session the server returned. The screen does not keep it.
 * @param notice A message from the session that just ended, or null. See `SessionGate`.
 */
export function LoginScreen({
  onSignedIn,
  notice,
}: {
  readonly onSignedIn: (session: Session) => void
  readonly notice: string | null
}) {
  const { t } = useTranslation(['common', 'auth'])

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [state, setState] = useState<State>({ kind: 'idle' })

  // So a refusal can put the cursor back where the correction is made. Which field that is
  // cannot be known — the server will not say which half was wrong, deliberately — so it is
  // the password, because a mistyped password is the likelier of the two and retyping it is
  // the cheaper.
  const passwordField = useRef<HTMLInputElement>(null)

  // Aborted on unmount only. A submit in flight is deliberately *not* cancelled by typing:
  // the attempt has reached the server and has written its audit row either way, and
  // abandoning the response would leave a clinician looking at a form that is already signed
  // in behind their back.
  const request = useRef<AbortController | null>(null)

  useEffect(() => () => request.current?.abort(), [])

  // After the commit, not on the line below the `setState` that produced the refusal. Both fields
  // are `disabled` while the submit is in flight, React batches the state change with everything
  // else in that callback, and `focus()` on a still-disabled input is a no-op — so focusing there
  // moved nothing and the cursor stayed where `autoFocus` had put it. Caught by
  // `Login.test.tsx`; it is not a jsdom artefact.
  useEffect(() => {
    if (state.kind === 'refused' && state.source === 'server') {
      passwordField.current?.focus()
    }
  }, [state])

  const submit = () => {
    if (state.kind === 'submitting') {
      return
    }

    // Checked here rather than with `required`, so the message is in the catalogue and
    // matches the refusals below instead of being whatever the browser says in whatever
    // language it is set to.
    if (username.trim() === '' || password === '') {
      setState({ kind: 'refused', message: t('auth:login.missing'), source: 'form' })
      return
    }

    const controller = new AbortController()
    request.current = controller
    setState({ kind: 'submitting' })

    login(username, password, controller.signal).then(
      (session) => {
        if (!controller.signal.aborted) {
          // The password is dropped from state on the way out. It has served its purpose and
          // there is no reason for it to stay in a live component's closure.
          setPassword('')
          onSignedIn(session)
        }
      },
      (cause: unknown) => {
        if (controller.signal.aborted) {
          return
        }

        // Anything that is not a refusal is reported as unavailable, including a thrown
        // value this code does not recognise. The two messages send the clinician to
        // different people, and "the box is not answering" is the safer of the two to be
        // wrong about: it does not accuse them of mistyping something they did not.
        const message =
          cause instanceof LoginRefusedError
            ? loginRefusalMessage(cause.code, t)
            : authUnavailableMessage(t)

        // Cleared, not kept. A field still holding a password that has just been refused
        // invites pressing the button again unchanged, and on a shared ward terminal it
        // leaves one on screen.
        setPassword('')

        // The cursor is moved by the effect above, once the fields are enabled again.
        setState({ kind: 'refused', message, source: 'server' })
      },
    )
  }

  return (
    <>
      {/* Identical markup and wording to the app bar's banner, from the same catalogue key.
          There is exactly one AKUTJOURNAL in this build. */}
      <header className="downtime-banner" role="banner">
        {t('app.banner')}
      </header>

      {/* Before anything can be typed — DECISIONS.md #49. Renders nothing on a box. */}
      <DeploymentNotice />
      <TranslationNotice />

      <main className="login">
        <div className="login__card">
          {/* Decorative and aria-hidden; the product name beside it carries the label. */}
          <span className="login__mark">
            <MarkLockup size={40} />
          </span>

          <h1 className="login__heading">{t('auth:login.heading')}</h1>
          <p className="login__intro">{t('auth:login.intro')}</p>

          {/* Before the form, so it is read before the fields rather than after a clinician
              has filled them in again. */}
          {notice !== null && (
            <p className="login__notice" role="status">
              {notice}
            </p>
          )}

          <form
            className="login__form"
            onSubmit={(event) => {
              event.preventDefault()
              submit()
            }}
          >
            <label className="login__label" htmlFor="login-username">
              {t('auth:login.username')}
            </label>
            <input
              id="login-username"
              className="login__field"
              type="text"
              value={username}
              // `username` so a password manager fills the pair, and nothing else the
              // browser might think it knows better: no correction and no capitalisation,
              // because the server lower-cases but does not repair.
              autoComplete="username"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              disabled={state.kind === 'submitting'}
              onChange={(event) => setUsername(event.target.value)}
            />

            <label className="login__label" htmlFor="login-password">
              {t('auth:login.password')}
            </label>
            <input
              id="login-password"
              ref={passwordField}
              className="login__field"
              type="password"
              value={password}
              autoComplete="current-password"
              disabled={state.kind === 'submitting'}
              onChange={(event) => setPassword(event.target.value)}
            />

            {/* Announced, because a refusal replaces nothing on screen — it appears, and a
                screen reader user pressing the button has to hear that it did. `alert`
                rather than `status`: this is the thing standing between them and the
                record. */}
            <div aria-live="assertive">
              {state.kind === 'refused' && (
                <p className="login__error" role="alert">
                  {state.message}
                </p>
              )}
            </div>

            <button className="login__submit" type="submit" disabled={state.kind === 'submitting'}>
              {state.kind === 'submitting' ? t('auth:login.submitting') : t('auth:login.submit')}
            </button>
          </form>

          {/* Last in the card and last in the tab order: a clinician logging in reaches the
              fields first, and somebody who needs another language finds this before they
              have typed anything wrong. */}
          <div className="login__language">
            <LanguageChoice />
          </div>
        </div>
      </main>
    </>
  )
}
